import React, { useEffect, useState } from "react";
import {
  Button,
  Modal,
  Form,
  Table,
  InputGroup,
  FormControl,
  Badge,
} from "react-bootstrap";
import {
  addStudent as createStudent,
  getStudents,
  updateUser as updateStudent,
  deleteUser as deleteStudent,
  updateUserPassword,
} from "../services/userService";
import { getCourses } from "../services/courseService";
import { getBatches } from "../services/batchService";
import Loader from "../components/Loader";
import { DownloadIcon, EyeIcon, PencilIcon, TrashIcon } from "lucide-react";
import { getAssignedTasksByUserId } from "../services/taskService";
import { useAuth } from "../context/AuthProvider";
import { toast } from "react-toastify";
import { TASK_STATUS, STUDENT_LEVELS, USER_ROLES } from "../utils";
import { Spinner } from "react-bootstrap";
import ToggleBtn from "../components/ToggleBtn";
import { updateUserStatus } from "../services/userService";
import { logStudentActivity } from "../services/activityService";
import ReadonlyStudentDashboard from "../components/ReadonlyStudentDashboard";

function Students() {
  const [show, setShow] = useState(false);
  const [students, setStudents] = useState([]);
  const [studentForm, setStudentForm] = useState({
    name: "",
    email: "",
    courses: [],
    batches: [],
    studentLevel: STUDENT_LEVELS.FREE_TRAINEE,
    enrollmentDate: "",
    role: USER_ROLES.STUDENT,
  });
  const [filter, setFilter] = useState("");
  const [batchFilter, setBatchFilter] = useState("");
  const [levelFilter, setLevelFilter] = useState("");
  const [sortKey, setSortKey] = useState("name");
  const [sortAsc, setSortAsc] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const [isEditing, setIsEditing] = useState(false);
  const [coursesList, setCoursesList] = useState([]);
  const [batchesList, setBatchesList] = useState([]);
  const handleClose = () => setShow(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    fetchStudents();
    fetchCourses();
    fetchBatches();
  }, []);

  const closeShowModal = () => {
    setShowModal(false);
    setSelectedStudent(null);
  };

  const handleViewDetails = (student) => {
    setSelectedStudent(student);
    setShowModal(true);
  };

  const fetchCourses = async () => {
    const courses = await getCourses();
    setCoursesList(courses);
  };

  const fetchBatches = async () => {
    const batches = await getBatches();
    setBatchesList(batches);
  };

  const fetchStudents = async () => {
    setLoading(true);
    const data = await getStudents();
    setStudents(data);
    setLoading(false);
  };

  const handleAddStudent = async (e) => {
    e.preventDefault();
    setBtnLoading(true);
    const studentId = await createStudent(studentForm);
    setStudents([...students, { ...studentForm, id: studentId }]);
    resetForm();
    handleClose();
    setBtnLoading(false);
    toast.success("Student added successfully");
  };

  const handleUpdateStudent = async (e) => {
    e.preventDefault();
    setBtnLoading(true);
    try {
      const originalStudent = students.find(s => s.id === studentForm.id);
      const roleChanged = originalStudent && originalStudent.role !== studentForm.role;
      
      // Confirm role change
      if (roleChanged) {
        const confirmChange = window.confirm(
          `Are you sure you want to change this user's role from "${originalStudent.role}" to "${studentForm.role}"?\n\n` +
          `This will move the user to a different category and may affect their access permissions.`
        );
        if (!confirmChange) {
          setBtnLoading(false);
          return;
        }
      }
      
      await updateStudent(studentForm.id, studentForm);
      setStudents(
        students.map((s) => (s.id === studentForm.id ? studentForm : s))
      );

      // Save student activity
      saveStudentActivity(studentForm);

      resetForm();
      setIsEditing(false);
      handleClose();
      setBtnLoading(false);
      
      if (roleChanged) {
        toast.success(`Student updated successfully! Role changed from ${originalStudent.role} to ${studentForm.role}.`);
        toast.info("The user will now appear in the appropriate section based on their new role.");
      } else {
        toast.success("Student updated successfully");
      }
    } catch (error) {
      setBtnLoading(false);
      toast.error("Failed to update student");
      console.error(error);
    }
  };

  const handleEdit = (id) => {
    const student = students.find((s) => s.id === id);
    if (student) {
      setIsEditing(true);
      setStudentForm(student);
      setSelectedStudent(student);
      setShow(true);
    }
  };

  const handleDelete = async (id) => {
    const student = students.find((s) => s.id === id);
    const studentName = student?.name || "this student";
    
    const confirmMessage = `Are you sure you want to delete ${studentName}?\n\n` +
      `This will permanently delete:\n` +
      `• User account and profile\n` +
      `• All attendance records\n` +
      `• All leave applications\n` +
      `• All task assignments\n` +
      `• All salary configurations and records\n` +
      `• All class records (if tutor)\n` +
      `• All activity history\n\n` +
      `This action cannot be undone!`;
      
    if (!window.confirm(confirmMessage)) return;
    
    setLoading(true);
    try {
      const result = await deleteStudent(id);
      setStudents(students.filter((s) => s.id !== id));
      setLoading(false);
      toast.success(`${studentName} and all associated records deleted successfully`);
      console.log("Deletion result:", result);
    } catch (error) {
      setLoading(false);
      toast.error(`Failed to delete ${studentName}: ${error.message}`);
      console.error("Delete error:", error);
    }
  };

  const resetForm = () =>
    setStudentForm({
      name: "",
      email: "",
      courses: [],
      batches: [],
      studentLevel: STUDENT_LEVELS.FREE_TRAINEE,
      enrollmentDate: "",
      role: USER_ROLES.STUDENT,
    });

  const filteredStudents = students
    .filter((s) => {
      // Text search filter
      const textMatch = Object.values(s).some((value) =>
        String(value).toLowerCase().includes(filter.toLowerCase())
      );

      // Batch filter
      const batchMatch =
        !batchFilter ||
        (s.batches && s.batches.some((batch) => batch.id === batchFilter));

      // Level filter
      const levelMatch = !levelFilter || s.studentLevel === levelFilter;

      return textMatch && batchMatch && levelMatch;
    })
    .sort((a, b) => {
      const aValue = String(a[sortKey]).toLowerCase();
      const bValue = String(b[sortKey]).toLowerCase();
      if (aValue < bValue) return sortAsc ? -1 : 1;
      if (aValue > bValue) return sortAsc ? 1 : -1;
      return 0;
    });

  const totalPages = Math.ceil(filteredStudents.length / pageSize);
  const paginatedStudents = filteredStudents.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const handleSetPassword = (student) => {
    setSelectedStudent(student);
    setNewPassword("");
    setConfirmPassword("");
    setShowPasswordModal(true);
  };

  const handleUpdatePassword = async () => {
    if (!selectedStudent?.id) {
      toast.error("Student not selected");
      return;
    }
    if (!newPassword || !confirmPassword) {
      toast.warn("Please fill in all password fields");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.warn("Passwords do not match!");
      return;
    }

    setBtnLoading(true);
    try {
      await updateUserPassword(selectedStudent.id, newPassword);
      toast.success("Password updated successfully!");
      setShowPasswordModal(false);
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      console.error(error);
      toast.error("Failed to update password");
    }
    setBtnLoading(false);
  };

  const handleToggleStatus = async (id, isActive) => {
    if (!id) return;
    if (user.id === id) {
      toast.error("You cannot change your own status");
      return;
    }
    if (window.confirm("Are you sure?") === false) return;

    setLoading(true);

    await updateUserStatus(id, isActive);
    setStudents((prev) =>
      prev.map((s) => (s.id === id ? { ...s, isActive } : s))
    );
    toast.success("Student status updated successfully!");
    setLoading(false);
  };

  const clearFilters = () => {
    setFilter("");
    setBatchFilter("");
    setLevelFilter("");
  };

  const saveStudentActivity = async (student) => {
    if (!student || !student.id) return;
    // Create activity log
    let text = "";
    Object.entries(student).forEach(async ([key, value]) => {
      // Only log if batches data changed from original
      if (
        key === "batches" &&
        JSON.stringify(value) !== JSON.stringify(selectedStudent?.batches || [])
      ) {
        text = `You have been assigned to batches: ${(value || [])
          .map((b) => b.name)
          .join(", ")}\n`;
        const activity = {
          studentId: student.id,
          text: text || "Your profile has been updated.",
          type: key,
        };

        await logStudentActivity(activity);
      } else if (
        key === "courses" &&
        JSON.stringify(value) !== JSON.stringify(selectedStudent?.courses || [])
      ) {
        text = `You have been assigned to courses: ${(value || [])
          .map((c) => c.name)
          .join(", ")}\n`;
        const activity = {
          studentId: student.id,
          text: text || "Your profile has been updated.",
          type: key,
        };

        await logStudentActivity(activity);
      }
    });
  };

  return (
    <div className="p-4 bg-white dark:bg-gray-900 text-gray-900 dark:text-white min-h-screen">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
        Students ({students.length})
      </h2>
      {loading && <Loader />}
      <div className="flex justify-between items-center gap-4 mb-4">
        <div className="flex gap-3 flex-1">
          <InputGroup className="flex-1">
            <FormControl
              placeholder="Search students..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </InputGroup>

          <Form.Select
            value={batchFilter}
            onChange={(e) => setBatchFilter(e.target.value)}
            style={{ maxWidth: "200px" }}
          >
            <option value="">All Batches</option>
            {batchesList.map((batch) => (
              <option key={batch.id} value={batch.id}>
                {batch.name}
              </option>
            ))}
          </Form.Select>

          <Form.Select
            value={levelFilter}
            onChange={(e) => setLevelFilter(e.target.value)}
            style={{ maxWidth: "200px" }}
          >
            <option value="">All Levels</option>
            {Object.entries(STUDENT_LEVELS).map(([key, value]) => (
              <option key={key} value={value}>
                {value.replace("_", " ").toUpperCase()}
              </option>
            ))}
          </Form.Select>
        </div>

        <div className="flex gap-2">
          {(filter || batchFilter || levelFilter) && (
            <Button variant="outline-secondary" onClick={clearFilters}>
              Clear Filters
            </Button>
          )}
          <Button
            variant="success"
            className="flex-col"
            onClick={() => {
              setShow(true);
              setIsEditing(false);
              resetForm();
            }}
          >
            + Add Student
          </Button>
        </div>
      </div>

      <Table bordered hover responsive>
        <thead className="table-light">
          <tr>
            <th>#</th>
            <th onClick={() => setSortKey("name")}>Name</th>
            <th onClick={() => setSortKey("email")}>Email</th>
            <th onClick={() => setSortKey("course")}>Courses</th>
            <th>Batches</th>
            <th>Level</th>
            <th>Resume</th>
            <th onClick={() => setSortKey("enrollmentDate")}>Enrolled On</th>
            <th>Status</th>
            <th colSpan={2}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {paginatedStudents.map((student, index) => (
            <tr key={index}>
              <td>{(currentPage - 1) * pageSize + index + 1}</td>
              <td>{student.name}</td>
              <td>{student.email}</td>
              <td>
                {student?.courses
                  ? (student.courses || []).map((c) => c.name).join(", ")
                  : "-"}
              </td>
              <td>
                {student?.batches
                  ? (student.batches || []).map((b) => b.name).join(", ")
                  : "-"}
              </td>
              <td>
                <Badge
                  style={{ 
                    border: "1px solid lightgray",
                    fontSize: "0.7rem",
                    padding: "0.25rem 0.4rem"
                  }}
                  bg={
                    student.studentLevel === STUDENT_LEVELS.FREE_TRAINEE
                      ? "secondary"
                      : student.studentLevel === STUDENT_LEVELS.PAID_TRAINEE
                      ? "primary"
                      : student.studentLevel === STUDENT_LEVELS.INTERN
                      ? "warning"
                      : student.studentLevel === STUDENT_LEVELS.EMPLOYEE
                      ? "success"
                      : "light"
                  }
                >
                  <span
                    className={
                      student.studentLevel === STUDENT_LEVELS.EMPLOYEE
                        ? "text-white"
                        : "text-black"
                    }
                    style={{ fontSize: "0.7rem", fontWeight: "500" }}
                  >
                    {student.studentLevel
                      ? Object.values(STUDENT_LEVELS)
                          .find((level) => level === student.studentLevel)
                          ?.replace("_", " ")
                          .toUpperCase() || "FREE TRAINEE"
                      : "FREE TRAINEE"}
                  </span>
                </Badge>
              </td>
              <td>
                {student.resumeUrl ? (
                  <a
                    href={student.resumeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 no-underline hover:underline text-primary"
                  >
                    Resume <DownloadIcon size={16} />
                  </a>
                ) : (
                  "-"
                )}
              </td>
              <td>{new Date(student.enrollmentDate).toDateString()}</td>
              <td>
                <span
                  className={
                    student.isActive
                      ? "text-success font-semibold flex items-center gap-1"
                      : "text-danger font-semibold flex items-center gap-1"
                  }
                >
                  <ToggleBtn
                    onToggle={(isChecked) =>
                      handleToggleStatus(student.id, isChecked)
                    }
                    isChecked={student.isActive}
                  />
                </span>
              </td>
              <td>
                <div className="flex gap-2">
                  <Button
                    variant="info"
                    onClick={() => handleViewDetails(student)}
                  >
                    <EyeIcon size={16} />
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => handleEdit(student.id)}
                  >
                    <PencilIcon size={16} />
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => handleDelete(student.id)}
                  >
                    <TrashIcon size={16} />
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleSetPassword(student)}
                  >
                    Set Password
                  </Button>{" "}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <div className="flex justify-between items-center">
        <div>
          Page {currentPage} of {totalPages}
        </div>
        <div>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            Prev
          </Button>{" "}
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
        </div>
      </div>

      <Modal show={show} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditing ? "Edit Student" : "Add New Student"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={isEditing ? handleUpdateStudent : handleAddStudent}>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter name"
                required
                value={studentForm.name}
                onChange={(e) =>
                  setStudentForm({ ...studentForm, name: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter email"
                required
                value={studentForm.email}
                onChange={(e) =>
                  setStudentForm({ ...studentForm, email: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Courses</Form.Label>
              <div>
                {coursesList.map((course) => (
                  <Form.Check
                    key={course.id}
                    type="checkbox"
                    label={course.name}
                    checked={(studentForm.courses || []).some(
                      (c) => c.id === course.id
                    )}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setStudentForm({
                          ...studentForm,
                          courses: [
                            ...(studentForm.courses || []),
                            { id: course.id, name: course.name },
                          ],
                        });
                      } else {
                        setStudentForm({
                          ...studentForm,
                          courses: (studentForm.courses || []).filter(
                            (c) => c.id !== course.id
                          ),
                        });
                      }
                    }}
                  />
                ))}
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Batches</Form.Label>
              <div>
                {batchesList.map((batch) => (
                  <Form.Check
                    key={batch.id}
                    type="checkbox"
                    label={batch.name}
                    checked={(studentForm.batches || []).some(
                      (b) => b.id === batch.id
                    )}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setStudentForm({
                          ...studentForm,
                          batches: [
                            ...(studentForm.batches || []),
                            { id: batch.id, name: batch.name },
                          ],
                        });
                      } else {
                        setStudentForm({
                          ...studentForm,
                          batches: (studentForm.batches || []).filter(
                            (b) => b.id !== batch.id
                          ),
                        });
                      }
                    }}
                  />
                ))}
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Level</Form.Label>
              <Form.Select
                value={studentForm.studentLevel}
                onChange={(e) =>
                  setStudentForm({
                    ...studentForm,
                    studentLevel: e.target.value,
                  })
                }
              >
                {Object.entries(STUDENT_LEVELS).map(([key, value]) => (
                  <option key={key} value={value}>
                    {value.replace("_", " ").toUpperCase()}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {/* Role selector - only visible for admins when editing */}
            {user?.role === 'admin' && isEditing && (
              <Form.Group className="mb-3">
                <Form.Label>User Role</Form.Label>
                <Form.Select
                  value={studentForm.role || USER_ROLES.STUDENT}
                  onChange={(e) =>
                    setStudentForm({
                      ...studentForm,
                      role: e.target.value,
                    })
                  }
                >
                  {Object.entries(USER_ROLES).map(([key, value]) => (
                    <option key={key} value={value}>
                      {value.replace("_", " ").toUpperCase()}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  ⚠️ Changing the role will move this user to a different category. Use with caution.
                </Form.Text>
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Enrollment Date</Form.Label>
              <Form.Control
                type="date"
                required
                value={studentForm.enrollmentDate}
                onChange={(e) =>
                  setStudentForm({
                    ...studentForm,
                    enrollmentDate: e.target.value,
                  })
                }
              />
            </Form.Group>

            <Button type="submit" variant="primary" disabled={btnLoading}>
              {btnLoading ? (
                <>
                  <Spinner
                    animation="border"
                    size="sm"
                    role="status"
                    className="text-white"
                  />
                  <span className="ml-2">
                    {isEditing ? "Updating..." : "Adding..."}
                  </span>
                </>
              ) : (
                <>{isEditing ? "Update Student" : "Add Student"}</>
              )}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Password Modal */}
      <Modal
        show={showPasswordModal}
        onHide={() => setShowPasswordModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Set Password for {selectedStudent?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>New Password</Form.Label>
              <Form.Control
                type="password"
                value={newPassword}
                placeholder="Enter new password"
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Confirm Password</Form.Label>
              <Form.Control
                type="password"
                value={confirmPassword}
                placeholder="Confirm new password"
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowPasswordModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdatePassword}
            disabled={btnLoading}
          >
            {btnLoading ? (
              <>
                <Spinner
                  animation="border"
                  size="sm"
                  role="status"
                  className="text-white"
                />
                <span className="ml-2">Updating...</span>
              </>
            ) : (
              <>Update Password</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Student Details Modal - Using Readonly Dashboard */}
      {selectedStudent && (
        <ReadonlyStudentDashboard
          student={selectedStudent}
          show={showModal}
          onHide={closeShowModal}
        />
      )}
    </div>
  );
}

export default Students;
